import React, { useState, useEffect } from 'react';
import Header from '../Components/Header';
import Footer from '../Components/Footer';
import TeamDetailPage from '../LabSoftware/TeamDetailPage';
import TeamData from './TeamData.json';
import { useParams } from 'react-router-dom';
import './Main.css';

function TeamDetailLayoutSelector() {
  const { slug } = useParams();
  const [selectedLayout, setSelectedLayout] = useState('default');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Get team member data to show name in selector
  const findTeamMemberBySlug = (slug) => {
    const leadershipMember = TeamData.leadership_team?.team_member_detail?.find(
      (member) => member.slug === slug
    );
    const operationMember = TeamData.operation_team?.team_member_detail?.find(
      (member) => member.slug === slug
    );
    if (leadershipMember) return leadershipMember;
    if (operationMember) return operationMember;
    return null;
  };

  const teamMember = findTeamMemberBySlug(slug);

  const layouts = {
    default: {
      name: 'Default',
      icon: '📄',
      description: 'Traditional layout with all sections',
      options: { layoutStyle: 'default', showBanner: true, showNewsletter: false, showFooter: false, showHeader: false }
    },
    card: {
      name: 'Card',
      icon: '🃏',
      description: 'Modern card design with gradient',
      options: { layoutStyle: 'card', showBanner: true, showNewsletter: false, showFooter: false, showHeader: false }
    },
    minimal: {
      name: 'Minimal',
      icon: '✨',
      description: 'Clean minimal design',
      options: { layoutStyle: 'minimal', showBanner: true, showNewsletter: false, showFooter: false, showHeader: false }
    },
    split: {
      name: 'Split Screen',
      icon: '📱',
      description: 'Full-width split layout',
      options: { layoutStyle: 'split', showBanner: true, showNewsletter: false, showFooter: false, showHeader: false }
    },
    hero: {
      name: 'Hero',
      icon: '🎯',
      description: 'Large hero section',
      options: { layoutStyle: 'hero', showBanner: true, showNewsletter: false, showFooter: false, showHeader: false }
    },
    grid: {
      name: 'Grid',
      icon: '⚏',
      description: 'Grid-based card layout',
      options: { layoutStyle: 'grid', showBanner: true, showNewsletter: false, showFooter: false, showHeader: false }
    },
    sidebar: {
      name: 'Sidebar',
      icon: '📋',
      description: 'Sidebar with sticky image',
      options: { layoutStyle: 'sidebar', showBanner: true, showNewsletter: false, showFooter: false, showHeader: false }
    },
    timeline: {
      name: 'Timeline',
      icon: '⏱️',
      description: 'Timeline style layout',
      options: { layoutStyle: 'timeline', showBanner: true, showNewsletter: false, showFooter: false, showHeader: false }
    },
    glass: {
      name: 'Glassmorphism',
      icon: '💎',
      description: 'Modern glass effect design',
      options: { layoutStyle: 'glass', showBanner: true, showNewsletter: false, showFooter: false, showHeader: false }
    },
    modern: {
      name: 'Modern Clean',
      icon: '✨',
      description: 'Clean, minimal professional template',
      options: { layoutStyle: 'modern', showBanner: false, showNewsletter: false, showFooter: false, showHeader: false }
    },
    modernCompact: {
      name: 'Modern Compact',
      icon: '📋',
      description: 'Sidebar style with sticky profile',
      options: { layoutStyle: 'modernCompact', showBanner: false, showNewsletter: false, showFooter: false, showHeader: false }
    },
    modernCentered: {
      name: 'Modern Centered',
      icon: '🎯',
      description: 'All centered with hero header',
      options: { layoutStyle: 'modernCentered', showBanner: false, showNewsletter: false, showFooter: false, showHeader: false }
    },
    modernSplit: {
      name: 'Modern Split',
      icon: '⚡',
      description: 'Two column layout',
      options: { layoutStyle: 'modernSplit', showBanner: false, showNewsletter: false, showFooter: false, showHeader: false }
    },
    modernMinimal: {
      name: 'Modern Minimal',
      icon: '🔹',
      description: 'Ultra clean minimal design',
      options: { layoutStyle: 'modernMinimal', showBanner: false, showNewsletter: false, showFooter: false, showHeader: false }
    },
    modernCardGrid: {
      name: 'Modern Card Grid',
      icon: '🎴',
      description: 'Card-based grid layout',
      options: { layoutStyle: 'modernCardGrid', showBanner: false, showNewsletter: false, showFooter: false, showHeader: false }
    },
    animatedHero: {
      name: 'Animated Hero',
      icon: '🎬',
      description: 'Animated hero with floating cards',
      options: { layoutStyle: 'animatedHero', showBanner: false, showNewsletter: false, showFooter: false, showHeader: false }
    },
    glassParallax: {
      name: 'Glass Parallax',
      icon: '💫',
      description: 'Glassmorphism with parallax effects',
      options: { layoutStyle: 'glassParallax', showBanner: false, showNewsletter: false, showFooter: false, showHeader: false }
    },
    cards3D: {
      name: '3D Cards',
      icon: '🎯',
      description: '3D tilt card effects',
      options: { layoutStyle: 'cards3D', showBanner: false, showNewsletter: false, showFooter: false, showHeader: false }
    }
  };

  return (
    <>
      <Header />
      <div className="main">
        {/* Layout Selector - Sticky at top */}
        <section 
          className="layout-options-section" 
          style={{ 
            background: '#f8f9fa', 
            paddingTop: '2rem', 
            paddingBottom: '2rem',
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
          }}
        >
          <div className="container">
            <div className="layout-selector-wrapper">
              <div className="text-center mb-4">
                <h3 className="mb-2" style={{ color: '#2d3748', fontWeight: '700' }}>
                  Team Detail Layout Selector
                </h3>
                {teamMember && (
                  <p className="text-muted mb-0">
                    Viewing: <strong>{teamMember.name}</strong> ({teamMember.role})
                  </p>
                )}
                <p className="text-muted mt-2" style={{ fontSize: '0.9rem' }}>
                  Click a layout style below to preview it
                </p>
              </div>
              <div className="layout-buttons">
                {Object.entries(layouts).map(([key, layout]) => (
                  <button
                    key={key}
                    className={`layout-btn ${selectedLayout === key ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedLayout(key);
                      window.scrollTo({ top: 200, behavior: 'smooth' });
                    }}
                    title={layout.description}
                  >
                    <span className="layout-icon">{layout.icon}</span>
                    <span className="layout-name">{layout.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Preview Section - Render TeamDetailPage without Header/Footer */}
        <div key={selectedLayout} style={{ minHeight: '100vh' }}>
          <TeamDetailPage layoutOptions={layouts[selectedLayout].options} />
        </div>
      </div>
      <Footer />
    </>
  );
}

export default TeamDetailLayoutSelector;
